import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import { format } from 'date-fns';
import React, { useMemo } from 'react';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useCustomerQueries } from '@dashboard/hooks/queries/useCustomerQueries';
import useUrlState from '@dashboard/hooks/useUrlState';
import useTableSort from '@dashboard/hooks/useTableSort';

import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import BlockedBadge from '@dashboard/components/BlockedBadge';
import ExportView from '@dashboard/components/ExportView';
import PageHeader from '@dashboard/components/PageHeader';
import SearchInput from '@dashboard/components/Form/components/SearchInput';

export default function Customers() {
  const { sellerId } = useAuth();
  const { getInfiniteCustomers } = useCustomerQueries();

  const [query, setParams] = useUrlState();
  const { sorting, onSortingChange, sort } = useTableSort();

  const search = query.search || '';

  const handleSearchChange = value => setParams({ search: value || undefined });
  const customersListQueryKey = React.useMemo(
    () => ['customers', 'list', sellerId, search, sort],
    [sellerId, search, sort],
  );

  // Fetch data
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isError, isLoading } =
    useInfiniteQuery({
      queryKey: customersListQueryKey,
      placeholderData: keepPreviousData,
      queryFn: async ({ pageParam }) =>
        await getInfiniteCustomers({
          search,
          limit: 10,
          skip: pageParam * 10,
          sort: JSON.stringify(sort),
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage.has_more) return undefined;
        return allPages.length;
      },
    });

  // Flatten paginated data
  const flatData = useMemo(() => data?.pages?.flatMap(page => page.data) ?? [], [data]);

  // Column definitions
  const columns = useMemo(
    () => [
      {
        accessorKey: 'email',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Customer" />,
        cell: ({ row }) => {
          const customer = row.original;
          return (
            <div className="max-w-xs">
              <div className="font-medium">
                {customer.email || ''} <BlockedBadge email={customer.email} />
              </div>
              <div className="text-sm text-gray-500 dark:text-muted-foreground">
                {customer.name || ''}
              </div>
            </div>
          );
        },
        enableSorting: true,
        meta: { className: 'w-full' },
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }) => (
          <DataGridColumnHeader column={column} title="Created date" className="justify-end" />
        ),
        cell: ({ row }) => {
          const createdAt = row.getValue('createdAt');
          return createdAt ? format(new Date(createdAt), 'd MMM, yyyy, HH:mm') : '';
        },
        enableSorting: true,
        meta: { className: 'text-right whitespace-nowrap' },
      },
    ],
    [],
  );

  // Empty state
  const isEmpty = !isLoading && flatData.length === 0;
  const emptyState = isEmpty ? (
    <EmptyState>
      <EmptyState.Title>{search ? 'No customers found' : 'No customers yet'}</EmptyState.Title>
      <EmptyState.Subtitle>
        {search ? "There aren't any results for this filter." : 'Your customers will show up here.'}
      </EmptyState.Subtitle>
    </EmptyState>
  ) : undefined;

  if (isError) {
    return (
      <div className="p-4">
        <div className="py-3 px-4">
          Unable to show customers. Please refresh or contact support.
        </div>
      </div>
    );
  }

  // Filter controls
  const filterControls = (
    <div className="flex items-center gap-2">
      <SearchInput
        onChange={handleSearchChange}
        name="search_customers"
        placeholder="Search by email"
        initialValue={search}
      />
      <ExportView
        collection="customers"
        label="customers"
        filters={{
          sort: JSON.stringify(sort),
          search,
        }}
      />
    </div>
  );

  return (
    <div className="p-4 space-y-4">
      <PageHeader title="Customers" />

      <DataGrid
        columns={columns}
        data={flatData}
        isLoading={isLoading}
        paginationMode="infinite"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sorting={sorting}
        onSortingChange={onSortingChange}
        toolbar={{
          customActions: filterControls,
        }}
        getRowHref={customer => `/customers/${customer._id}`}
        emptyState={emptyState}
        emptyMessage="No customers found"
      />
    </div>
  );
}
